import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useLocation, useParams } from 'react-router-dom'
import {
	ActionIcon,
	Box,
	Group,
	Menu,
	Popover,
	Progress,
	Stack,
	Text,
	Tooltip,
	createStyles,
	keyframes,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import {
	Activity,
	ArrowUpRight,
	ChevronDown,
	ChevronUp,
	ClipboardCheck,
	MapPin,
	Pinned,
	PinnedOff,
	Plus,
	Refresh,
	Send,
	UserPlus,
	Users,
	Wifi,
	WifiOff,
} from 'tabler-icons-react'
import useOrgLiveStatus from '../hooks/useOrgLiveStatus'
import useLiveTripPin, { getLiveTripPinKey } from '../hooks/useLiveTripPin'
import OrgSearchBox from './OrgSearchBox'

const TRIP_ROTATION_INTERVAL_MS = 10_000
const TRIP_TRANSITION_MS = 280
const EXPERIENCE_ACTIONS = [
	{ label: 'Add Check-in', path: 'manage/checkin/new', icon: ClipboardCheck },
	{ label: 'Add Staff', path: 'manage/staff', icon: UserPlus },
	{ label: 'New Message Blast', path: 'messaging/blasts/new', icon: Send },
]
const scrollInNext = keyframes({
	from: { transform: 'translateY(100%)', opacity: 0 },
	to: { transform: 'translateY(0)', opacity: 1 },
})
const scrollOutNext = keyframes({
	from: { transform: 'translateY(0)', opacity: 1 },
	to: { transform: 'translateY(-100%)', opacity: 0 },
})
const scrollInPrevious = keyframes({
	from: { transform: 'translateY(-100%)', opacity: 0 },
	to: { transform: 'translateY(0)', opacity: 1 },
})
const scrollOutPrevious = keyframes({
	from: { transform: 'translateY(0)', opacity: 1 },
	to: { transform: 'translateY(100%)', opacity: 0 },
})

const useStyles = createStyles((theme) => ({
	bar: {
		display: 'flex',
		alignItems: 'center',
		gap: 16,
		minHeight: 40,
		padding: '0 16px',
		paddingBottom: 'env(safe-area-inset-bottom, 0px)',
		borderTop: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
		background: theme.colorScheme === 'dark' ? theme.colors.dark[7] : '#fafbfc',
		color:
			theme.colorScheme === 'dark'
				? theme.colors.gray[3]
				: theme.colors.gray[7],
		fontSize: 12,
		fontVariantNumeric: 'tabular-nums',
		'@media (max-width: 62em)': { gap: 8 },
		'@media (max-width: 48em)': { gap: 6, paddingLeft: 10, paddingRight: 10 },
	},
	brand: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
		fontSize: 10,
		fontWeight: 700,
		letterSpacing: '0.09em',
		whiteSpace: 'nowrap',
	},
	separator: {
		width: 1,
		alignSelf: 'stretch',
		flexShrink: 0,
		background:
			theme.colorScheme === 'dark'
				? theme.colors.dark[4]
				: theme.colors.gray[3],
	},
	tabletOnly: {
		display: 'none',
		'@media (min-width: 48em)': { display: 'block' },
	},
	search: {
		display: 'block',
		width: 150,
		height: 26,
		flexShrink: 0,
		padding: '0 8px',
		border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
		borderRadius: 4,
		background:
			theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
		color: 'inherit',
		font: 'inherit',
		'&::placeholder': {
			color:
				theme.colorScheme === 'dark'
					? theme.colors.dark[1]
					: theme.colors.gray[6],
		},
		'&:focus-visible': {
			outline: `2px solid ${theme.colors.blue[5]}`,
			outlineOffset: 1,
		},
		'@media (max-width: 62em)': { width: 125 },
	},
	dot: { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },
	tripButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		flex: '0 1 auto',
		gap: 7,
		height: 32,
		padding: '0 6px',
		marginLeft: -6,
		border: 0,
		borderRadius: 4,
		background: 'transparent',
		color: 'inherit',
		cursor: 'pointer',
		font: 'inherit',
		whiteSpace: 'nowrap',
		'&:hover': {
			background:
				theme.colorScheme === 'dark'
					? theme.colors.dark[5]
					: theme.colors.gray[1],
		},
		'&:focus-visible': {
			outline: `2px solid ${theme.colors.blue[5]}`,
			outlineOffset: 1,
		},
		'&:disabled': { cursor: 'default', opacity: 0.7 },
	},
	metric: {
		display: 'flex',
		alignItems: 'center',
		gap: 7,
		whiteSpace: 'nowrap',
	},
	tripDisplay: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		minWidth: 0,
		'@media (max-width: 48em)': { gap: 6 },
	},
	tripViewport: {
		position: 'relative',
		height: 32,
		minWidth: 0,
		overflow: 'hidden',
	},
	tripRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 16,
		height: 32,
		minWidth: 0,
		'@media (max-width: 48em)': { gap: 6 },
		'&[data-motion="incoming"]': {
			animation: `${scrollInNext} ${TRIP_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both`,
			'&[data-direction="previous"]': { animationName: `${scrollInPrevious}` },
		},
		'&[data-motion="outgoing"]': {
			position: 'absolute',
			inset: 0,
			pointerEvents: 'none',
			animation: `${scrollOutNext} ${TRIP_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both`,
			'&[data-direction="previous"]': { animationName: `${scrollOutPrevious}` },
		},
		'@media (prefers-reduced-motion: reduce)': {
			'&[data-motion="incoming"]': { animation: 'none' },
			'&[data-motion="outgoing"]': { display: 'none' },
		},
	},
	progressMetric: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		minWidth: 0,
		whiteSpace: 'nowrap',
		'@media (max-width: 48em)': { gap: 6 },
	},
	tripSelector: { display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 },
	tripName: {
		maxWidth: 250,
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		fontWeight: 600,
		'@media (max-width: 48em)': { maxWidth: 100 },
		'@media (max-width: 36em)': { maxWidth: 72 },
	},
	tripNavigation: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		flexShrink: 0,
	},
	tripControls: {
		display: 'flex',
		alignItems: 'center',
		gap: 2,
		flexShrink: 0,
	},
	progressTrack: { width: 64, '@media (max-width: 62em)': { display: 'none' } },
	progressCount: { '@media (max-width: 62em)': { display: 'none' } },
	wideOnly: { '@media (max-width: 88em)': { display: 'none' } },
	paceMetric: { '@media (max-width: 36em)': { display: 'none' } },
	desktopOnly: { '@media (max-width: 48em)': { display: 'none' } },
	connection: {
		display: 'flex',
		alignItems: 'center',
		gap: 7,
		marginLeft: 'auto',
		whiteSpace: 'nowrap',
	},
	freshness: {
		color:
			theme.colorScheme === 'dark'
				? theme.colors.dark[1]
				: theme.colors.gray[6],
		'@media (max-width: 75em)': { display: 'none' },
	},
	dropdown: {
		maxWidth: 'calc(100vw - 24px)',
		padding: 16,
	},
	tripList: { maxHeight: 'min(360px, 60vh)', overflowY: 'auto' },
	trip: {
		display: 'block',
		padding: '12px 0',
		textDecoration: 'none',
		color: 'inherit',
		borderTop: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]}`,
		'&:hover': {
			color: theme.colors.blue[theme.colorScheme === 'dark' ? 4 : 7],
		},
	},
}))

const number = (value) => new Intl.NumberFormat().format(value ?? 0)
const ageLabel = (seconds) =>
	seconds < 10
		? 'just now'
		: seconds < 60
			? `${seconds}s ago`
			: `${Math.floor(seconds / 60)}m ago`

const TripStatusMetrics = ({
	trip,
	orgSlug,
	classes,
	tripIndex,
	tripCount,
	rateWindowMinutes,
	motion,
	direction,
	actionsOpened = false,
	onActionsOpenChange,
}) => {
	const leaving = motion === 'outgoing'
	return (
		<div
			className={classes.tripRow}
			data-motion={motion}
			data-direction={direction}
			aria-hidden={leaving || undefined}
			inert={leaving || undefined}
		>
			<div className={classes.progressMetric}>
				<div className={classes.tripSelector}>
					<Tooltip
						disabled={leaving}
						label={`${trip.name} · Trip ${tripIndex + 1} of ${tripCount > 1 ? `${tripCount}` : ''}`}
						withinPortal
					>
						<span
							className={classes.tripName}
							aria-label={
								leaving ? undefined : `Current live trip: ${trip.name}`
							}
						>
							{trip.name}
						</span>
					</Tooltip>
				</div>

				<strong>{trip.progress}%</strong>
				<div className={classes.metric}>
					<Progress
						value={trip.progress || 0}
						size={4}
						color='red'
						className={classes.progressTrack}
						aria-label={leaving ? undefined : `${trip.name} check-in progress`}
					/>
					<span className={classes.progressCount}>
						{number(trip.completed)} / {number(trip.total)} checked in
					</span>
				</div>
			</div>
			<Tooltip
				disabled={leaving}
				label={`Current check-ins rate per hour`}
				withinPortal
			>
				<div className={`${classes.metric} ${classes.paceMetric}`}>
					<Activity size={15} aria-hidden='true' />
					<strong>{number(trip.currentCheckInRatePerHour)}/hr</strong>
				</div>
			</Tooltip>

			<div className={`${classes.metric} ${classes.wideOnly}`}>
				<Users size={14} aria-hidden='true' />
				{number(trip.staffCount)} staff assigned
			</div>

			<div className={`${classes.metric} ${classes.wideOnly}`}>
				<MapPin size={14} aria-hidden='true' />
				{number(trip.stationCount)} stations
			</div>
			<Menu
				opened={!leaving && actionsOpened}
				onChange={leaving ? undefined : onActionsOpenChange}
				position='top-end'
				withinPortal
				shadow='md'
				width={220}
				zIndex={150}
				closeOnEscape
				returnFocus
			>
				<Menu.Target>
					<ActionIcon
						size={24}
						variant='subtle'
						color='gray'
						style={{ flexShrink: 0 }}
						disabled={leaving}
						aria-label={leaving ? undefined : `Actions for ${trip.name}`}
					>
						<Plus size={15} aria-hidden='true' />
					</ActionIcon>
				</Menu.Target>
				<Menu.Dropdown aria-label={`Actions for ${trip.name}`}>
					<Menu.Label>
						<Text size='xs' truncate>{trip.name}</Text>
					</Menu.Label>
					{EXPERIENCE_ACTIONS.map(({ label, path, icon: Icon }) => (
						<Menu.Item
							key={path}
							component={Link}
							to={`/${orgSlug}/experiences/${trip.slug}/${path}`}
							icon={<Icon size={15} aria-hidden='true' />}
						>
							{label}
						</Menu.Item>
					))}
				</Menu.Dropdown>
			</Menu>
		</div>
	)
}

const LiveStatusBar = ({ orgSlug, userId, orgId }) => {
	const { classes } = useStyles()
	const showExperienceStatus = useMediaQuery('(min-width: 48em)')
	const [opened, setOpened] = useState(false)
	const [actionsOpened, setActionsOpened] = useState(false)
	const [selectedTripId, setSelectedTripId] = useState(null)
	const [rotationVersion, setRotationVersion] = useState(0)
	const [rotationDirection, setRotationDirection] = useState(1)
	const [leavingTrip, setLeavingTrip] = useState(null)
	const previousTripRef = useRef(null)
	const transitionTimerRef = useRef(null)
	const {
		data,
		isOnline,
		isRefreshing,
		hasError,
		ageSeconds,
		isStale,
		refresh,
	} = useOrgLiveStatus(orgSlug)
	const { pinnedTripId, canPersistPin, setPinnedTripId } = useLiveTripPin(
		userId,
		orgId,
		data?.trips,
	)
	const trips = data?.trips || []
	const tripOrder = JSON.stringify(trips.map((trip) => trip.id))
	const activePinId = trips.some((trip) => trip.id === pinnedTripId)
		? pinnedTripId
		: null
	const isPinned = Boolean(activePinId)
	const selectedTripIndex = Math.max(
		0,
		trips.findIndex((trip) => trip.id === (activePinId || selectedTripId)),
	)
	const selectedTrip = trips[selectedTripIndex]
	const showPinControl =
		canPersistPin && selectedTrip && (trips.length > 1 || isPinned)

	useEffect(() => {
		if (activePinId) setSelectedTripId(activePinId)
	}, [activePinId])

	useEffect(() => {
		if (!showExperienceStatus) setActionsOpened(false)
	}, [showExperienceStatus])

	useLayoutEffect(() => {
		const previousTrip = previousTripRef.current
		previousTripRef.current = selectedTrip
		if (previousTrip?.id === selectedTrip?.id) return
		setActionsOpened(false)
		clearTimeout(transitionTimerRef.current)
		if (
			!previousTrip ||
			!selectedTrip ||
			window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
		) {
			setLeavingTrip(null)
			return
		}
		setLeavingTrip(previousTrip)
		transitionTimerRef.current = setTimeout(
			() => setLeavingTrip(null),
			TRIP_TRANSITION_MS,
		)
	}, [selectedTrip])

	useEffect(() => () => clearTimeout(transitionTimerRef.current), [])

	useEffect(() => {
		const tripIds = JSON.parse(tripOrder)
		if (!showExperienceStatus || tripIds.length < 2 || isPinned || actionsOpened) return
		const intervalId = setInterval(() => {
			if (document.hidden) return
			setRotationDirection(1)
			setSelectedTripId((currentId) => {
				const currentIndex = Math.max(0, tripIds.indexOf(currentId))
				return tripIds[(currentIndex + 1) % tripIds.length]
			})
		}, TRIP_ROTATION_INTERVAL_MS)
		return () => clearInterval(intervalId)
	}, [tripOrder, rotationVersion, isPinned, actionsOpened, showExperienceStatus])

	const skipTrip = (direction) => {
		const nextTripId =
			trips[(selectedTripIndex + direction + trips.length) % trips.length].id
		setRotationDirection(direction)
		setSelectedTripId(nextTripId)
		if (isPinned) setPinnedTripId(nextTripId)
		// Give a manually selected trip a full 10 seconds before rotating again.
		setRotationVersion((version) => version + 1)
	}
	const togglePin = () => {
		setSelectedTripId(selectedTrip.id)
		setPinnedTripId(isPinned ? null : selectedTrip.id)
		setRotationVersion((version) => version + 1)
	}

	const connectionLabel = !isOnline
		? 'Offline'
		: hasError
			? 'Retrying'
			: isStale
				? 'Stale'
				: !data
					? 'Connecting'
					: 'Connected'
	const connectionColor =
		!isOnline || hasError || isStale ? '#d98b19' : data ? '#2f9e6b' : '#868e96'
	const freshness =
		ageSeconds === null
			? 'Waiting for first update'
			: `Updated ${ageLabel(ageSeconds)}`
	const connectionHint = !isOnline
		? `Your browser is offline. ${data ? 'Showing the last successful update.' : 'Waiting for a connection.'}`
		: hasError
			? `Live status could not refresh. ${data ? 'Showing the last successful update.' : 'No status data yet.'} Retrying every 30 seconds.`
			: `Live status API: ${connectionLabel.toLowerCase()}. ${freshness}. Refreshes every 30 seconds while this tab is visible.`

	return (
		<Box className={classes.bar} aria-label='Organization live trip status'>
			<div className={classes.brand}>LIVEHQ</div>
			<span className={classes.separator} aria-hidden='true' />
			<OrgSearchBox
				orgSlug={orgSlug}
				className={classes.tabletOnly}
				inputClassName={classes.search}
			/>
			<span
				className={`${classes.separator} ${classes.tabletOnly}`}
				aria-hidden='true'
			/>
			<Popover
				opened={opened}
				onChange={setOpened}
				position='top-start'
				width={360}
				withinPortal
				shadow='md'
				radius='md'
				zIndex={150}
			>
				<Popover.Target>
					<button
						className={classes.tripButton}
						onClick={() => setOpened((value) => !value)}
						disabled={!data}
						aria-expanded={opened}
						aria-label='View live trips'
					>
						<span className={classes.metric}>
							<span
								className={classes.dot}
								style={{ background: trips.length ? '#DC1817' : '#868e96' }}
								aria-hidden='true'
							/>
							<strong>{data ? trips.length : '—'}</strong>
							<span>Live {trips.length === 1 ? 'Trip' : 'Trips'}</span>
						</span>
						<ChevronUp
							size={13}
							aria-hidden='true'
							style={{ transform: opened ? 'rotate(180deg)' : undefined }}
						/>
					</button>
				</Popover.Target>
				<Popover.Dropdown className={classes.dropdown}>
					<Group position='apart' mb={4}>
						<Text size='sm' fw={700}>
							Live trips
						</Text>
						<Text size='xs' color='dimmed'>
							{trips.length} in progress
						</Text>
					</Group>
					<Text size='xs' color='dimmed' mb='sm'>
						Based on each trip’s dates and time zone.
					</Text>
					<div className={classes.tripList}>
						{trips.length === 0 && (
							<Text size='sm' py='sm'>
								No trips are live right now.
							</Text>
						)}
						{trips.map((trip) => (
							<Link
								key={trip.id}
								to={`/${orgSlug}/experiences/${trip.slug}/overview`}
								className={classes.trip}
								onClick={() => setOpened(false)}
								aria-label={`Open ${trip.name} overview`}
							>
								<Stack spacing={7}>
									<Group position='apart' noWrap>
										<Text size='sm' fw={600}>
											{trip.name}
										</Text>
										<ArrowUpRight
											size={15}
											style={{ flexShrink: 0 }}
											aria-hidden='true'
										/>
									</Group>
									<Progress
										value={trip.progress}
										size={4}
										color='red'
										aria-label={`${trip.name} check-in progress`}
									/>
									<Group position='apart' spacing='xs'>
										<Text size='xs' color='dimmed'>
											{number(trip.completed)} / {number(trip.total)} checked in
											· {trip.progress}%
										</Text>
										<Text size='xs' fw={600}>
											{number(trip.currentCheckInRatePerHour)}/hr
										</Text>
									</Group>
								</Stack>
							</Link>
						))}
					</div>
					{data?.nextTrip && (
						<Text size='xs' color='dimmed' mt='xs'>
							Next up: {data.nextTrip.name} ·{' '}
							{new Intl.DateTimeFormat(undefined, {
								month: 'short',
								day: 'numeric',
								timeZone: 'UTC',
							}).format(new Date(data.nextTrip.startDate))}
						</Text>
					)}
					<Group position='apart' mt='sm'>
						<Text size='xs' color='dimmed'>
							{freshness}
							{hasError || isStale || !isOnline ? ' · May be out of date' : ''}
						</Text>
						<Text
							component={Link}
							to={`/${orgSlug}/admin/experiences`}
							size='xs'
							color='blue'
							onClick={() => setOpened(false)}
						>
							All trips →
						</Text>
					</Group>
				</Popover.Dropdown>
			</Popover>
			{showExperienceStatus && selectedTrip && (
				<span className={classes.separator} aria-hidden='true' />
			)}
			{showExperienceStatus && selectedTrip && (
				<div className={classes.tripDisplay}>
					{(trips.length > 1 || showPinControl) && (
						<div className={classes.tripControls}>
							{trips.length > 1 && (
								<div className={classes.tripNavigation}>
									<Tooltip label='Previous live trip' withinPortal>
										<ActionIcon
											size={20}
											style={{ height: 12, minHeight: 12 }}
											variant='subtle'
											color='gray'
											onClick={() => skipTrip(-1)}
											aria-label='Previous live trip'
										>
											<ChevronUp size={12} aria-hidden='true' />
										</ActionIcon>
									</Tooltip>
									<Tooltip label='Next live trip' withinPortal>
										<ActionIcon
											size={20}
											style={{ height: 12, minHeight: 12 }}
											variant='subtle'
											color='gray'
											onClick={() => skipTrip(1)}
											aria-label='Next live trip'
										>
											<ChevronDown size={12} aria-hidden='true' />
										</ActionIcon>
									</Tooltip>
								</div>
							)}
							{showPinControl && (
								<Tooltip
									label={
										isPinned
											? 'Unpin experience and resume rotation'
											: 'Pin experience and pause rotation'
									}
									withinPortal
								>
									<ActionIcon
										size={24}
										variant='subtle'
										color='gray'
										onClick={togglePin}
										aria-label={
											isPinned ? 'Unpin live experience' : 'Pin live experience'
										}
										aria-pressed={isPinned}
									>
										{isPinned ? (
											<PinnedOff size={15} aria-hidden='true' />
										) : (
											<Pinned size={15} aria-hidden='true' />
										)}
									</ActionIcon>
								</Tooltip>
							)}
						</div>
					)}
					<div className={classes.tripViewport} aria-label='Current trip stats'>
						<TripStatusMetrics
							key={selectedTrip.id}
							trip={selectedTrip}
							orgSlug={orgSlug}
							classes={classes}
							tripIndex={selectedTripIndex}
							tripCount={trips.length}
							rateWindowMinutes={data?.rateWindowMinutes}
							motion={leavingTrip ? 'incoming' : undefined}
							direction={rotationDirection === 1 ? 'next' : 'previous'}
							actionsOpened={actionsOpened}
							onActionsOpenChange={setActionsOpened}
						/>
						{leavingTrip && (
							<TripStatusMetrics
								key={`leaving-${leavingTrip.id}`}
								trip={leavingTrip}
								orgSlug={orgSlug}
								classes={classes}
								tripIndex={selectedTripIndex}
								tripCount={trips.length}
								rateWindowMinutes={data?.rateWindowMinutes}
								motion='outgoing'
								direction={rotationDirection === 1 ? 'next' : 'previous'}
							/>
						)}
					</div>
				</div>
			)}
			{data && trips.length === 0 && (
				<span className={classes.desktopOnly}>
					{data.upcomingTripCount > 0 && `${data.upcomingTripCount} upcoming`}
				</span>
			)}
			<Tooltip label={connectionHint} withinPortal>
				<div className={classes.connection}>
					{isOnline ? (
						<Wifi size={14} color={connectionColor} aria-hidden='true' />
					) : (
						<WifiOff size={14} color={connectionColor} aria-hidden='true' />
					)}
					<span role='status' aria-live='polite'>
						<span className={classes.desktopOnly} aria-hidden='true'>
							{connectionLabel}
						</span>
						<span
							style={{
								position: 'absolute',
								width: 1,
								height: 1,
								overflow: 'hidden',
								clipPath: 'inset(50%)',
							}}
						>
							{connectionLabel}
						</span>
					</span>
				</div>
			</Tooltip>
			<Tooltip
				label={isRefreshing ? 'Refreshing status…' : 'Refresh status'}
				withinPortal
			>
				<ActionIcon
					size={28}
					variant='subtle'
					color='gray'
					onClick={refresh}
					disabled={!isOnline || isRefreshing}
					aria-label='Refresh live trip status'
					aria-busy={isRefreshing}
				>
					<Refresh size={14} aria-hidden='true' />
				</ActionIcon>
			</Tooltip>
		</Box>
	)
}

const OrgAdminStatusBar = () => {
	const { orgSlug } = useParams()
	const location = useLocation()
	const currentOrg = useSelector((state) => state.user.currentOrg)
	const orgRole = useSelector((state) => state.org.orgRole)
	const authUser = useSelector((state) => state.user.authUser)
	const userType = authUser?.userType
	const canView =
		orgSlug &&
		currentOrg?.orgSlug === orgSlug &&
		currentOrg?.isActive !== false &&
		((currentOrg?.role || orgRole) === 'admin' || userType === 'superAdmin') &&
		!location.pathname.startsWith('/control')

	return canView ? (
		<LiveStatusBar
			key={getLiveTripPinKey(authUser?.id, currentOrg?.orgId) || orgSlug}
			orgSlug={orgSlug}
			userId={authUser?.id}
			orgId={currentOrg?.orgId}
		/>
	) : null
}

export default OrgAdminStatusBar
